import { useCallback, useEffect, useRef, useState } from 'react';
import { useRecoilState } from 'recoil';
import { toast } from 'sonner';
import { ChevronDown, ChevronRight, Filter, Trash2, Upload } from 'lucide-react';

import { apiClient, type UserDoc } from '@/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import {
  userDocsFilterState,
  userDocsState,
  userDocsTenantStatusState
} from '@/state/project';

import {
  extensionToIcon,
  formatBytes,
  formatRelativeDate
} from './userDocsUtils';

type Props = {
  onFilterChange?: () => void;
};

export default function UserDocsSection({ onFilterChange }: Props) {
  const [docs, setDocs] = useRecoilState(userDocsState);
  const [activeFilter, setActiveFilter] = useRecoilState(userDocsFilterState);
  const [tenantStatus, setTenantStatus] = useRecoilState(
    userDocsTenantStatusState
  );
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [previews, setPreviews] = useState<Record<string, string>>({});
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const refreshDocs = useCallback(async () => {
    setLoading(true);
    try {
      const [listResponse, filterResponse] = await Promise.all([
        apiClient.listUserDocs(),
        apiClient.getUserDocFilter()
      ]);
      setDocs(listResponse.docs);
      setTenantStatus(listResponse.tenant_status);
      setActiveFilter(filterResponse.file_names);
    } catch (error) {
      console.error('Failed to load user docs', error);
      toast.error('Could not load your uploaded documents');
    } finally {
      setLoading(false);
    }
  }, [setDocs, setActiveFilter, setTenantStatus]);

  useEffect(() => {
    refreshDocs();
  }, [refreshDocs]);

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFilesPicked = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const picked = event.target.files ? Array.from(event.target.files) : [];
    event.target.value = '';
    if (!picked.length) return;
    setLoading(true);
    try {
      const response = await apiClient.uploadUserDocs(picked);
      response.results.forEach((result) => {
        if (result.error) {
          toast.error(`${result.file_name}: ${result.error}`);
        } else if (result.duplicate) {
          toast.info(`${result.file_name} is already in your library`);
        } else {
          toast.success(
            `${result.file_name} uploaded (${result.chunk_count} chunks)`
          );
        }
      });
      if (response.docs) {
        setDocs(response.docs);
        if (response.tenant_status) {
          setTenantStatus(response.tenant_status);
        }
      } else {
        await refreshDocs();
      }
    } catch (error) {
      console.error('Upload failed', error);
      toast.error('Upload failed — check the backend logs');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (fileName: string) => {
    if (!confirm(`Delete ${fileName}?`)) return;
    setLoading(true);
    try {
      await apiClient.deleteUserDoc(fileName);
      toast.success(`${fileName} removed`);
      if (activeFilter?.includes(fileName)) {
        const remaining = activeFilter.filter((name) => name !== fileName);
        const next = remaining.length ? remaining : null;
        await apiClient.setUserDocFilter(next);
        setActiveFilter(next);
        onFilterChange?.();
      }
      await refreshDocs();
    } catch (error) {
      console.error('Delete failed', error);
      toast.error('Delete failed');
    } finally {
      setLoading(false);
    }
  };

  const togglePreview = async (doc: UserDoc) => {
    const next = expanded === doc.file_name ? null : doc.file_name;
    setExpanded(next);
    if (next && !(next in previews)) {
      try {
        const response = await apiClient.getUserDocPreview(next);
        setPreviews((prev) => ({ ...prev, [next]: response.preview }));
      } catch (error) {
        console.error('Preview failed', error);
        setPreviews((prev) => ({ ...prev, [next]: '(preview unavailable)' }));
      }
    }
  };

  const toggleFilter = async (fileName: string) => {
    const current = activeFilter ?? [];
    const next = current.includes(fileName)
      ? current.filter((name) => name !== fileName)
      : [...current, fileName];
    const normalized = next.length ? next : null;
    try {
      await apiClient.setUserDocFilter(normalized);
      setActiveFilter(normalized);
      onFilterChange?.();
    } catch (error) {
      console.error('Filter update failed', error);
      toast.error('Could not update search scope');
    }
  };

  const clearFilter = async () => {
    try {
      await apiClient.setUserDocFilter(null);
      setActiveFilter(null);
      onFilterChange?.();
    } catch (error) {
      console.error('Filter clear failed', error);
      toast.error('Could not clear search scope');
    }
  };

  const filterLabel = activeFilter?.length
    ? `Searching ${activeFilter.length} of ${docs.length} docs`
    : 'Searching all docs';

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <h3 className="text-sm font-semibold text-foreground">User Docs</h3>
        <p className="text-xs text-muted-foreground">
          Documents you have uploaded to the Weaviate knowledge base. Enable the
          Personal RAG setting to let the assistant search them.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          onClick={handleUploadClick}
          disabled={loading}
          className="gap-1.5"
        >
          <Upload className="h-3.5 w-3.5" />
          Upload
        </Button>
        <Popover>
          <PopoverTrigger asChild>
            <Button
              size="sm"
              variant="outline"
              className="gap-1.5"
              disabled={docs.length === 0}
            >
              <Filter className="h-3.5 w-3.5" />
              {filterLabel}
            </Button>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            className="w-72 p-2 bg-popover text-popover-foreground border-border"
          >
            <div className="flex flex-col gap-1">
              <p className="px-1 py-0.5 text-[11px] text-muted-foreground">
                Pick which documents the assistant may search. Leave all
                unchecked to search everything.
              </p>
              {docs.map((doc) => {
                const checked = activeFilter?.includes(doc.file_name) ?? false;
                return (
                  <label
                    key={doc.file_name}
                    className="flex items-center gap-2 rounded px-2 py-1.5 text-xs hover:bg-accent cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleFilter(doc.file_name)}
                      className="h-3.5 w-3.5"
                    />
                    <span className="truncate">{doc.file_name}</span>
                  </label>
                );
              })}
              {activeFilter?.length ? (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearFilter}
                  className="justify-start text-xs"
                >
                  Clear filter
                </Button>
              ) : null}
            </div>
          </PopoverContent>
        </Popover>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={handleFilesPicked}
        />
      </div>

      {tenantStatus && tenantStatus !== 'ACTIVE' && tenantStatus !== 'UNKNOWN' ? (
        <Card className="border-dashed">
          <CardContent className="p-4 text-xs text-muted-foreground">
            Tenant status: {tenantStatus}. Ask an admin to reactivate your
            knowledge base if this persists.
          </CardContent>
        </Card>
      ) : null}

      {docs.length === 0 ? (
        <Card className="border-dashed bg-card/40">
          <CardContent className="p-6 flex flex-col items-center gap-2 text-center">
            <Upload className="h-5 w-5 text-muted-foreground" />
            <h4 className="text-sm font-semibold text-foreground">
              No documents uploaded yet
            </h4>
            <p className="max-w-xs text-xs text-muted-foreground">
              Upload markdown, PDF, DOCX, CSV, XML, or TXT files. They are
              chunked and stored in your private Weaviate tenant.
            </p>
            <Button size="sm" onClick={handleUploadClick} className="mt-1">
              Upload files
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="flex flex-col gap-2">
          {docs.map((doc) => (
            <DocCard
              key={doc.file_name}
              doc={doc}
              expanded={expanded === doc.file_name}
              preview={previews[doc.file_name]}
              onToggle={() => togglePreview(doc)}
              onDelete={() => handleDelete(doc.file_name)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function DocCard({
  doc,
  expanded,
  preview,
  onToggle,
  onDelete
}: {
  doc: UserDoc;
  expanded: boolean;
  preview: string | undefined;
  onToggle: () => void;
  onDelete: () => void;
}) {
  const { icon: Icon, className: iconClass } = extensionToIcon(doc.file_type);
  return (
    <Card>
      <CardContent className="p-3 flex flex-col gap-2">
        <button
          type="button"
          className="flex flex-wrap items-center gap-x-2 gap-y-1 text-left"
          onClick={onToggle}
        >
          {expanded ? (
            <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
          )}
          <Icon className={cn('h-4 w-4 flex-shrink-0', iconClass)} />
          <span
            className="flex-1 min-w-0 truncate text-sm font-medium text-foreground"
            title={doc.file_name}
          >
            {doc.file_name}
          </span>
          <Badge variant="secondary" className="text-[10px]">
            {formatBytes(doc.byte_size)}
          </Badge>
          <span className="text-[11px] text-muted-foreground whitespace-nowrap">
            {formatRelativeDate(doc.uploaded_at)}
          </span>
          <Button
            variant="ghost"
            size="icon"
            onClick={(event) => {
              event.stopPropagation();
              onDelete();
            }}
            className="h-7 w-7 text-muted-foreground hover:text-destructive"
            aria-label={`Delete ${doc.file_name}`}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </button>
        <div className="flex items-center gap-2 pl-6 text-[11px] text-muted-foreground">
          <span>{doc.chunk_count} chunks</span>
        </div>
        {expanded ? (
          <pre className="whitespace-pre-wrap break-words rounded bg-muted/60 p-3 text-[11px] text-foreground max-h-48 overflow-auto">
            {preview ?? 'Loading preview…'}
          </pre>
        ) : null}
      </CardContent>
    </Card>
  );
}
